// src/services/onboardingAPI.ts
// ✅ ENHANCED VERSION: Added missing methods for integration verification

import { authService } from './authService';

const BASE_URL = `${process.env.REACT_APP_API_BASE_URL || ''}/onboarding`;

// Memory leak prevention - request deduplication and caching
const pendingRequests = new Map<string, Promise<any>>();
const responseCache = new Map<string, { data: any, timestamp: number }>();
const CACHE_TTL = 30000; // 30 seconds

// Abort controller for cleanup
let globalAbortController: AbortController | null = null;

const getAbortSignal = () => {
  if (!globalAbortController || globalAbortController.signal.aborted) {
    globalAbortController = new AbortController();
  }
  return globalAbortController.signal;
};

// Cache cleanup to prevent memory growth
const cleanupCache = () => {
  const now = Date.now();
  const keysToDelete: string[] = [];
  
  responseCache.forEach((entry, key) => {
    if (now - entry.timestamp > CACHE_TTL) {
      keysToDelete.push(key);
    }
  });
  
  keysToDelete.forEach(key => responseCache.delete(key));
};

// Auto cleanup every minute
setInterval(cleanupCache, 60000);

// Deduplicated request wrapper that maintains original response format
const makeRequest = async (key: string, requestFn: () => Promise<Response>, skipCache = false) => {
  // Check cache first (unless skipped)
  if (!skipCache) {
    const cached = responseCache.get(key);
    if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
      console.log(`📋 OnboardingAPI: Using cached data for ${key}`);
      return cached.data;
    }
  }

  // Check if request already pending
  if (pendingRequests.has(key)) {
    console.log(`⏳ OnboardingAPI: Request already pending for ${key}`);
    return await pendingRequests.get(key);
  }

  // Make new request
  console.log(`🔗 OnboardingAPI: Making request to ${key}`);
  
  const requestPromise = (async () => {
    try {
      const response = await requestFn();
      
      console.log(`📡 OnboardingAPI: Response status: ${response.status}`);
      
      if (!response.ok) {
        return {
          success: false,
          error: { message: `HTTP ${response.status}: ${response.statusText}` }
        };
      }

      const data = await response.json();
      console.log('✅ OnboardingAPI: Response data:', data);
      
      const result = {
        success: true,
        data: data
      };
      
      // Cache successful responses
      if (!skipCache) {
        responseCache.set(key, { data: result, timestamp: Date.now() });
      }
      
      return result;
    } catch (error) {
      console.error(`❌ OnboardingAPI: Request failed for ${key}:`, error);
      return {
        success: false,
        error: { message: error instanceof Error ? error.message : 'Unknown error' }
      };
    }
  })();

  pendingRequests.set(key, requestPromise);
  
  try {
    return await requestPromise;
  } finally {
    pendingRequests.delete(key);
  }
};

export const onboardingAPI = {
  // Get all role templates
  getRoleTemplates: async () => {
    const key = `${BASE_URL}/role-templates`;
    
    return makeRequest(key, () => 
      authService.makeAuthenticatedRequest(key, {
        signal: getAbortSignal()
      })
    );
  },

  // Get onboarding sessions with filtering
  getSessions: async (status?: string) => {
    const params = status ? `?status=${encodeURIComponent(status)}` : '';
    const key = `${BASE_URL}/sessions${params}`;
    
    return makeRequest(key, () =>
      authService.makeAuthenticatedRequest(key, {
        signal: getAbortSignal()
      })
    );
  },

  // ✅ NEW: Get active sessions specifically 
  getActiveSessions: async () => {
    return onboardingAPI.getSessions('Draft,Active,Pending,Ready');
  },

  // ✅ NEW: Get completed sessions specifically
  getCompletedSessions: async () => {
    return onboardingAPI.getSessions('Complete,Partial');
  },

  // Get single session by ID
  getSession: async (sessionId: string) => {
    const key = `${BASE_URL}/sessions/${sessionId}`;
    
    return makeRequest(key, () =>
      authService.makeAuthenticatedRequest(key, {
        signal: getAbortSignal()
      })
    );
  },

  // Create new onboarding session
  createSession: async (sessionData: any) => {
    const key = `${BASE_URL}/sessions`;
    
    // Don't cache POST requests
    return makeRequest(`${key}-create-${Date.now()}`, () =>
      authService.makeAuthenticatedRequest(key, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(sessionData),
        signal: getAbortSignal()
      }), true
    );
  },

  // Update session
  updateSession: async (sessionId: string, updateData: any) => {
    const key = `${BASE_URL}/sessions/${sessionId}`;
    
    return makeRequest(`${key}-update-${Date.now()}`, () =>
      authService.makeAuthenticatedRequest(key, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updateData),
        signal: getAbortSignal()
      }), true
    );
  },

  // Get items for a specific category in a session
  getCategoryItems: async (sessionId: string, categoryId: string) => {
    const key = `${BASE_URL}/sessions/${sessionId}/categories/${categoryId}/items`;
    
    return makeRequest(key, () =>
      authService.makeAuthenticatedRequest(key, {
        signal: getAbortSignal()
      })
    );
  },

  // Select an item in a session
  selectItem: async (sessionId: string, itemData: any) => {
    const key = `${BASE_URL}/sessions/${sessionId}/select-item`;
    
    // Don't cache item selection requests
    return makeRequest(`${key}-${Date.now()}`, () =>
      authService.makeAuthenticatedRequest(key, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(itemData),
        signal: getAbortSignal()
      }), true
    );
  },

  // Remove an item from selection
  removeItem: async (sessionId: string, categoryId: string, itemId: string) => {
    const key = `${BASE_URL}/sessions/${sessionId}/remove-item`;
    
    return makeRequest(`${key}-${Date.now()}`, () =>
      authService.makeAuthenticatedRequest(key, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ categoryId, itemId }),
        signal: getAbortSignal()
      }), true
    );
  },

  // Mark item as pending
  markItemPending: async (sessionId: string, request: any) => {
    const key = `${BASE_URL}/sessions/${sessionId}/mark-pending`;
    
    return makeRequest(`${key}-${Date.now()}`, () =>
      authService.makeAuthenticatedRequest(key, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(request),
        signal: getAbortSignal()
      }), true
    );
  },

  // Create attestation
  createAttestation: async (sessionId: string, request: any) => {
    const key = `${BASE_URL}/sessions/${sessionId}/attest`;
    
    return makeRequest(`${key}-${Date.now()}`, () =>
      authService.makeAuthenticatedRequest(key, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(request),
        signal: getAbortSignal()
      }), true
    );
  },

  // Complete attestation for a category
  completeAttestation: async (sessionId: string, categoryId: string, attestationData: any) => {
    const key = `${BASE_URL}/sessions/${sessionId}/categories/${categoryId}/attest`;
    
    return makeRequest(`${key}-${Date.now()}`, () =>
      authService.makeAuthenticatedRequest(key, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(attestationData),
        signal: getAbortSignal()
      }), true
    );
  },

  // ✅ FIXED: Complete session - calls correct endpoint
  completeSession: async (sessionId: string, request: any = {}) => {
    const key = `${BASE_URL}/sessions/${sessionId}/complete`;
    
    console.log(`🎯 OnboardingAPI: Completing session ${sessionId}`);
    
    return makeRequest(`${key}-${Date.now()}`, () =>
      authService.makeAuthenticatedRequest(key, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(request),
        signal: getAbortSignal()
      }), true
    );
  },

  // ✅ FIXED: Complete onboarding - alias for completeSession
  completeOnboarding: async (sessionId: string, request: any = {}) => {
    return onboardingAPI.completeSession(sessionId, request);
  },

  // Cancel session
  cancelSession: async (sessionId: string, reason?: string) => {
    const key = `${BASE_URL}/sessions/${sessionId}/cancel`;
    
    return makeRequest(`${key}-${Date.now()}`, () =>
      authService.makeAuthenticatedRequest(key, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason }),
        signal: getAbortSignal()
      }), true
    );
  },

  // Delete session
  deleteSession: async (sessionId: string) => {
    const key = `${BASE_URL}/sessions/${sessionId}`;
    
    return makeRequest(`${key}-delete-${Date.now()}`, () =>
      authService.makeAuthenticatedRequest(key, {
        method: 'DELETE',
        signal: getAbortSignal()
      }), true
    );
  },

  // ✅ NEW: Verify session completion and assignments
  verifySessionCompletion: async (sessionId: string) => {
    const key = `${BASE_URL}/sessions/${sessionId}/verify`;
    
    return makeRequest(key, () =>
      authService.makeAuthenticatedRequest(key, {
        signal: getAbortSignal()
      })
    );
  },

  // ✅ NEW: Get session statistics and summary
  getSessionSummary: async (sessionId: string) => {
    const key = `${BASE_URL}/sessions/${sessionId}/summary`;
    
    return makeRequest(key, () =>
      authService.makeAuthenticatedRequest(key, {
        signal: getAbortSignal()
      })
    );
  },

  // ✅ NEW: Get onboarding analytics
  getOnboardingAnalytics: async (dateRange?: { from: string; to: string }) => {
    const params = dateRange ? 
      `?from=${encodeURIComponent(dateRange.from)}&to=${encodeURIComponent(dateRange.to)}` : 
      '';
    const key = `${BASE_URL}/analytics${params}`;
    
    return makeRequest(key, () =>
      authService.makeAuthenticatedRequest(key, {
        signal: getAbortSignal()
      })
    );
  },

  // Role template management methods
createRoleTemplate: async (templateData: any) => {
    const url = `${BASE_URL}/role-templates`;
    const cacheKey = `${url}-create-${Date.now()}`;
    
    return makeRequest(cacheKey, () =>
      authService.makeAuthenticatedRequest(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(templateData),
        signal: getAbortSignal()
      }), true
    );
  },

  updateRoleTemplate: async (templateId: string, updateData: any) => {
    const key = `${BASE_URL}/role-templates/${templateId}`;
    
    return makeRequest(`${key}-update-${Date.now()}`, () =>
      authService.makeAuthenticatedRequest(key, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updateData),
        signal: getAbortSignal()
      }), true
    );
  },

  // ✅ NEW: Bulk operations
  bulkUpdateSessions: async (sessionIds: string[], updateData: any) => {
    const key = `${BASE_URL}/sessions/bulk`;
    
    return makeRequest(`${key}-bulk-${Date.now()}`, () =>
      authService.makeAuthenticatedRequest(key, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionIds, updateData }),
        signal: getAbortSignal()
      }), true
    );
  },

  bulkDeleteSessions: async (sessionIds: string[], reason?: string) => {
    const key = `${BASE_URL}/sessions/bulk`;
    
    return makeRequest(`${key}-bulk-delete-${Date.now()}`, () =>
      authService.makeAuthenticatedRequest(key, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionIds, reason }),
        signal: getAbortSignal()
      }), true
    );
  },

  // ✅ NEW: Session health check
  checkSessionHealth: async (sessionId: string) => {
    const key = `${BASE_URL}/sessions/${sessionId}/health`;
    
    return makeRequest(key, () =>
      authService.makeAuthenticatedRequest(key, {
        signal: getAbortSignal()
      })
    );
  },

  // ✅ NEW: Get session assignment history
  getSessionAssignmentHistory: async (sessionId: string) => {
    const key = `${BASE_URL}/sessions/${sessionId}/assignments`;
    
    return makeRequest(key, () =>
      authService.makeAuthenticatedRequest(key, {
        signal: getAbortSignal()
      })
    );
  },

  // Cancel all pending requests (cleanup)
  cancelAllRequests: () => {
    if (globalAbortController) {
      globalAbortController.abort();
      globalAbortController = null;
    }
    pendingRequests.clear();
    console.log('🚫 OnboardingAPI: All requests cancelled');
  },

  // Clear cache
  clearCache: () => {
    responseCache.clear();
    console.log('🗑️ OnboardingAPI: Cache cleared');
  },

  // Get cache stats for debugging
  getCacheStats: () => ({
    cacheSize: responseCache.size,
    pendingRequests: pendingRequests.size,
    cacheKeys: Array.from(responseCache.keys()),
    pendingKeys: Array.from(pendingRequests.keys())
  })
};

// Default export for files that expect it
export default onboardingAPI;